// Shared save logic for the Data Entry Issues transaction edit row.
// LedgerUpdate writes every flag it receives, so the payload always carries the row's current values.
import _ from 'lodash';
import { toast } from 'react-toastify';

const toAccountId = (value) => (value && Number(value) > 0 ? Number(value) : 0);

export function buildLedgerPayload(item, overrides = {}) {
    return {
        id: item.id,
        transactionTypeId: item.transactionTypeId || 0,
        transactionType: item.transactionType,
        accountId: item.accountId || 0,
        newAccountId: toAccountId(item.newAccountId),
        needsBetterDescription: !!item.needsBetterDescription,
        transferCodedIncorrectly: !!item.transferCodedIncorrectly,
        cpaReview: !!item.cpaReview,
        pcCheck: !!item.pcCheck,
        accountantComment: item.accountantComment || '',
        internalNotes: item.internalNotes || '',
        taxableFringeBenefit: !!item.taxableFringeBenefit,
        vendor1099: !!item.vendor1099,
        taxReporting: !!item.taxReporting,
        isArchived: !!item.isArchived,
        benefitTypeId: item.benefitTypeId || 0,
        employeeName: item.employeeName || '',
        clientComment: item.clientComment || '',
        accountChangeApprovedByClient: !!item.accountChangeApprovedByClient,
        clientReviewStatus: !!item.clientReviewStatus,
        accountantReviewStatus: !!item.accountantReviewStatus,
        descriptionNeedsEmployeeName: !!item.descriptionNeedsEmployeeName,
        clientApprovedTaxableFringeBenefit: !!item.clientApprovedTaxableFringeBenefit,
        accountantPostedTaxableFringeBenefit: !!item.accountantPostedTaxableFringeBenefit,
        memoOrDescription: item.memoOrDescription || '',
        ...overrides
    };
}

// Edit-row values for a row (copies — the row object itself is never edited).
export function initialEditData(item) {
    return {
        newAccountId: toAccountId(item.newAccountId),
        memoOrDescription: item.memoOrDescription || '',
        taxableFringeBenefit: !!item.taxableFringeBenefit,
        needsBetterDescription: !!item.needsBetterDescription,
        cpaReview: !!item.cpaReview,
        accountantComment: item.accountantComment || '',
        internalNotes: item.internalNotes || ''
    };
}

// Decides how to save: 'none' (nothing changed), 'tfbOnly' (TFB is the sole change) or 'full'
// (LedgerUpdate with every edited field, then UpdateDescription when the memo changed and memoToQuickBooks is on).
export function planSave(item, editData, reportType, options = {}) {
    const memoToQuickBooks = options.memoToQuickBooks !== false;
    const before = initialEditData(item);
    const tfbChanged = before.taxableFringeBenefit !== !!editData.taxableFringeBenefit;
    const memoChanged = before.memoOrDescription !== (editData.memoOrDescription || '');
    const otherChanged = before.newAccountId !== toAccountId(editData.newAccountId)
        || before.needsBetterDescription !== !!editData.needsBetterDescription
        || before.cpaReview !== !!editData.cpaReview
        || before.accountantComment !== (editData.accountantComment || '')
        || before.internalNotes !== (editData.internalNotes || '')
        || memoChanged;

    if (!tfbChanged && !otherChanged) {
        return { mode: 'none' };
    }
    if (tfbChanged && !otherChanged) {
        return {
            mode: 'tfbOnly',
            payload: buildLedgerPayload(item, { taxableFringeBenefit: !!editData.taxableFringeBenefit, updateTaxableFringeBenefitOnly: true })
        };
    }
    return {
        mode: 'full',
        payload: buildLedgerPayload(item, {
            newAccountId: toAccountId(editData.newAccountId),
            memoOrDescription: editData.memoOrDescription || '',
            taxableFringeBenefit: !!editData.taxableFringeBenefit,
            needsBetterDescription: !!editData.needsBetterDescription,
            cpaReview: !!editData.cpaReview,
            accountantComment: editData.accountantComment || '',
            internalNotes: editData.internalNotes || ''
        }),
        descriptionBody: memoChanged && memoToQuickBooks ? {
            id: item.id,
            memoOrDescription: editData.memoOrDescription || '',
            reportType: reportType,
            createdByName: null
        } : null
    };
}

// "{number} {name}" for the New Account # column; falls back to the accounts list.
export function formatNewAccountLabel(item, accounts) {
    const fromApi = item.newAccountName != null ? String(item.newAccountName).trim() : '';
    if (fromApi !== '') return fromApi;
    const id = toAccountId(item.newAccountId);
    if (id && accounts && accounts.length > 0) {
        const acc = accounts.find(x => Number(x.accountID) === id);
        if (acc) return acc.accountNumber != null ? `${acc.accountNumber} ${acc.name}` : acc.name;
    }
    return '';
}

// Scrolling the page or a table scroller closes an open (portalled) menu;
// scrolling inside the menu list itself does not.
const PAGE_SCROLLERS = ['table-responsive-material', 'app-main-content-wrapper', 'app-main-content'];
function isPageScroll(event) {
    const t = event && event.target;
    if (!t || t === document || t === document.documentElement || t === document.body) return true;
    return !!(t.classList && PAGE_SCROLLERS.some(c => t.classList.contains(c)));
}

// react-select props for New Account pickers: menu renders on <body> (never clipped by the table
// box), opens upwards when there is no room below, scrolls after ~6 options, long names wrap.
export function accountSelectMenuProps() {
    return {
        menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
        menuPlacement: 'auto',
        maxMenuHeight: 240,
        closeMenuOnScroll: isPageScroll,
        styles: {
            control: base => ({ ...base, minHeight: 38 }),
            valueContainer: base => ({ ...base, overflow: 'visible' }),
            singleValue: base => ({ ...base, overflow: 'visible', textOverflow: 'clip', maxWidth: '100%' }),
            menuPortal: base => ({ ...base, zIndex: 9999 }),
            menu: base => ({ ...base, zIndex: 9999, minWidth: 260 }),
            option: base => ({ ...base, whiteSpace: 'normal', wordBreak: 'break-word' })
        }
    };
}

// react-select options from the Accounts API data ("number name", sorted, with a leading "Select").
export function toAccountOptions(accountsData) {
    const options = _.sortBy(accountsData || [], ['accountNumber', 'name']).map(value => ({ value: value.accountID, label: (value.accountNumber != null ? value.accountNumber + ' ' + value.name : value.name) }));
    options.unshift({ value: 0, label: 'Select' });
    return options;
}

// Starts the planned save (needs page props LedgerUpdate / UPDATE_DESCRIPTION_DATA, GetReportData() and state companyId); false when nothing changed.
// options.memoToQuickBooks = false keeps the memo in the LedgerUpdate only (no QuickBooks call).
export function startSave(page, item, editData, reportType, options) {
    const plan = planSave(item, editData, reportType, options);
    if (plan.mode === 'none') {
        page.setState({ editIndex: -1 });
        return false;
    }
    page.setState({ loading: true, pendingDescriptionBody: plan.descriptionBody || null, pendingTfbOnlySave: plan.mode === 'tfbOnly' }, () => {
        page.props.LedgerUpdate(plan.payload, page.state.companyId);
    });
    return true;
}

// LEDGER_UPDATE result: chain the memo update (QBO) when pending, otherwise finish.
export function handleLedgerResult(page, result) {
    if (result && result.transactionStatus === 0) {
        if (page.state.pendingDescriptionBody) {
            page.props.UPDATE_DESCRIPTION_DATA(page.state.pendingDescriptionBody);
            return;
        }
        const message = page.state.pendingTfbOnlySave ? "TFB updated successfully." : "Transaction updated successfully";
        page.setState({ editIndex: -1, pendingTfbOnlySave: false }, () => {
            toast.success(message);
            page.GetReportData();
        });
        return;
    }
    page.setState({ loading: false, editIndex: -1, pendingDescriptionBody: null, pendingTfbOnlySave: false }, () => {
        toast.error((result && result.resultMsg) || "Update failed");
    });
}

// UpdateDescription result for a memo save started here; returns false for other callers (e.g. Ignore/Revert).
export function handleMemoResult(page, result) {
    if (!page.state.pendingDescriptionBody) return false;
    const ok = result && result.transactionStatus === 0;
    page.setState({ editIndex: -1, pendingDescriptionBody: null, pendingTfbOnlySave: false }, () => {
        if (ok) {
            toast.success("Transaction updated successfully");
        } else {
            toast.error((result && result.resultMsg) || "Memo/Description was not updated in QuickBooks");
        }
        page.GetReportData();
    });
    return true;
}
